/**
 * Recommended Tenders tab (URL id: weekly-review).
 *
 * Top opportunities per tracked keyword for BD teams to scan this week.
 */

import React, { useMemo, useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Badge, LoadingSpinner, EmptyState, getAPIClient } from '@tracekit/core';
import { useWorkspace, useAuthContext } from '@tracekit/core';
import {
  CalendarDays,
  RefreshCw,
  Sparkles,
  Tag,
  ExternalLink,
  Flame,
  Clock3,
  Star,
  AlertTriangle,
} from 'lucide-react';
import { OpportunityDashboardCard } from '../OpportunityDashboardCard';
import { OpportunityDetailModal } from '../OpportunityDetailModal';
import { MatchScoreTooltip, type MatchExplain } from '../MatchScoreTooltip';
import { TrackingOwnerChip } from '../TrackingOwnerChip';
import { WeeklyReviewTrackedKeywordsPanel } from './WeeklyReviewTrackedKeywordsPanel';
import { useWeeklyGlobalOpportunities } from '../../../hooks/useWeeklyGlobalOpportunities';
import { useWeeklyReviewTrackedKeywords } from '../../../hooks/useWeeklyReviewTrackedKeywords';
import { useWeeklyReviewStalenessResync } from '../../../hooks/useWeeklyReviewStalenessResync';
import {
  TOP_OPPORTUNITIES_PER_KEYWORD,
  WEEKLY_REVIEW_DEADLINE_HORIZON_DAYS,
  WEEKLY_REVIEW_EXPIRING_DAYS,
  WEEKLY_REVIEW_RECENTLY_EXPIRED_LOOKBACK_DAYS,
  getGeographicZoneDisplayLabel,
  getOpportunityDaysUntilDeadline,
  getPersistedMatchScore,
  getTrackingOwnerName,
  mapSearchEntityToGovtOpportunity,
  patchWeeklyOpportunityTrackingData,
  type KeywordOpportunitySection,
  type WeeklyDigestOpportunity,
} from '../../../utils/weeklyGlobalOpportunities';
import { formatOpportunityDeadlineDate } from '../../../utils/opportunityUtils';
import type { OpportunityEntity } from '../../../api/opportunities';
import type { GovtOpportunityEntity } from '../../../pages/government-data/OpportunitiesDashboard';

function DigestScoreColumn({
  matchScore,
  explain,
}: {
  matchScore?: number;
  explain?: MatchExplain | null;
}) {
  if (matchScore == null || matchScore <= 0) {
    return null;
  }
  return (
    <div className="flex flex-col items-end gap-0.5 flex-shrink-0 min-w-0">
      <MatchScoreTooltip score={matchScore} variant="badge" explain={explain} />
    </div>
  );
}

function RecentlyExpiredStrip({
  items,
  isLoading,
  onViewDetails,
}: {
  items: WeeklyDigestOpportunity[];
  isLoading?: boolean;
  onViewDetails: (opportunity: OpportunityEntity) => void;
}) {
  if (isLoading || items.length === 0) return null;

  return (
    <details className="rounded-lg border border-theme-border/70 bg-theme-surface/20 text-theme-text-secondary">
      <summary className="cursor-pointer list-none flex flex-wrap items-center gap-2 px-3 py-2 text-xs select-none [&::-webkit-details-marker]:hidden">
        <AlertTriangle className="h-3.5 w-3.5 text-theme-text-secondary" />
        <span className="font-medium">Recently expired (missed)</span>
        <Badge variant="default" className="text-[10px]">
          {items.length}
        </Badge>
        <span className="text-[11px]">
          Closed in the last {WEEKLY_REVIEW_RECENTLY_EXPIRED_LOOKBACK_DAYS} days — expand to review
        </span>
      </summary>
      <ul className="space-y-1 border-t border-theme-border/60 px-2 py-2">
        {items.map((opp) => {
          const deadlineLabel = opp.data?.deadline
            ? formatOpportunityDeadlineDate(opp.data.deadline)
            : null;
          const daysAgo = getOpportunityDaysUntilDeadline(opp);
          const expiredLabel =
            daysAgo != null && daysAgo < 0
              ? `${Math.abs(daysAgo)} day${Math.abs(daysAgo) === 1 ? '' : 's'} ago`
              : null;

          return (
            <li key={`expired-${opp.id}`}>
              <button
                type="button"
                onClick={() => onViewDetails(opp)}
                className="w-full text-left rounded-md px-2 py-1.5 hover:bg-theme-surface/60 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-2">
                    {opp.data?.title || 'Untitled opportunity'}
                  </p>
                  <DigestScoreColumn
                    matchScore={opp.matchScore ?? getPersistedMatchScore(opp)}
                    explain={
                      (opp.data as { match_explain?: MatchExplain | null } | undefined)
                        ?.match_explain ?? null
                    }
                  />
                </div>
                <p className="text-xs text-theme-text-secondary mt-0.5 line-clamp-1">
                  {opp.weeklyKeyword ? `${opp.weeklyKeyword} · ` : ''}
                  {opp.data?.source || 'Unknown source'}
                  {deadlineLabel ? ` · Due ${deadlineLabel}` : ''}
                  {expiredLabel ? ` (${expiredLabel})` : ''}
                </p>
              </button>
            </li>
          );
        })}
      </ul>
    </details>
  );
}

function DigestStripSkeleton({ title, icon }: { title: string; icon: React.ReactNode }) {
  return (
    <section
      className="rounded-xl border border-slate-200 bg-white shadow-sm p-6 space-y-3 motion-safe:animate-pulse dark:border-slate-700 dark:bg-slate-900"
      aria-busy="true"
      aria-label={`Loading ${title}`}
    >
      <div className="flex items-center gap-2">
        {icon}
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
      </div>
      <div className="space-y-2">
        <div className="h-10 rounded-lg bg-slate-100 dark:bg-slate-800" />
        <div className="h-10 rounded-lg bg-slate-100 dark:bg-slate-800" />
      </div>
    </section>
  );
}

function KeywordSectionSkeleton() {
  return (
    <div
      className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3"
      aria-busy="true"
      aria-label="Loading tenders for this keyword"
    >
      {Array.from({ length: 3 }).map((_, index) => (
        <div
          key={index}
          className="rounded-xl border border-slate-200 bg-white shadow-sm p-6 motion-safe:animate-pulse dark:border-slate-700 dark:bg-slate-900"
        >
          <div className="h-4 w-3/4 rounded bg-slate-100 dark:bg-slate-800" />
          <div className="mt-3 h-3 w-1/2 rounded bg-slate-100 dark:bg-slate-800" />
          <div className="mt-6 h-8 w-20 rounded bg-slate-100 dark:bg-slate-800" />
        </div>
      ))}
    </div>
  );
}

function DigestStrip({
  title,
  icon,
  items,
  emptyLabel,
  isLoading,
  onViewDetails,
}: {
  title: string;
  icon: React.ReactNode;
  items: WeeklyDigestOpportunity[];
  emptyLabel: string;
  isLoading?: boolean;
  onViewDetails: (opportunity: OpportunityEntity) => void;
}) {
  if (isLoading) {
    return <DigestStripSkeleton title={title} icon={icon} />;
  }

  return (
    <section className="rounded-xl border border-theme-border bg-theme-surface/40 p-4 space-y-3">
      <div className="flex items-center gap-2">
        {icon}
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
        <Badge variant="default" className="text-xs">
          {items.length}
        </Badge>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-theme-text-secondary">{emptyLabel}</p>
      ) : (
        <ul className="space-y-2">
          {items.map((opp) => (
            <li key={`${title}-${opp.id}`}>
              <button
                type="button"
                onClick={() => onViewDetails(opp)}
                className="w-full text-left rounded-lg border border-transparent hover:border-theme-border hover:bg-white/60 dark:hover:bg-slate-800/60 px-2 py-2 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-2">
                    {opp.data?.title || 'Untitled opportunity'}
                  </p>
                  <DigestScoreColumn
                    matchScore={opp.matchScore ?? getPersistedMatchScore(opp)}
                    explain={
                      (opp.data as { match_explain?: MatchExplain | null } | undefined)
                        ?.match_explain ?? null
                    }
                  />
                </div>
                <p className="text-xs text-theme-text-secondary mt-1 line-clamp-1">
                  {opp.weeklyKeyword ? `${opp.weeklyKeyword} · ` : ''}
                  {opp.data?.organization || 'Unknown org'}
                </p>
                <TrackingOwnerChip
                  name={getTrackingOwnerName(opp.data)}
                  trackingStatus={
                    typeof opp.data?.tracking_status === 'string' ? opp.data.tracking_status : null
                  }
                />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function KeywordSection({
  section,
  onViewDetails,
  onUpdateTracking,
  onGenerateProposal,
}: {
  section: KeywordOpportunitySection;
  onViewDetails: (opportunity: GovtOpportunityEntity) => void;
  onUpdateTracking: (entityId: string, trackingStatus: string) => void;
  onGenerateProposal: (opportunity: GovtOpportunityEntity) => void;
}) {
  const sectionPending = section.isLoading;

  return (
    <section className="space-y-4 pb-8 border-b border-theme-border last:border-b-0 last:pb-0">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Tag className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0" aria-hidden="true" />
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 truncate">
            {section.keyword}
          </h3>
          {section.confidencePct != null && section.confidencePct > 0 && (
            <Badge
              variant="default"
              className="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
              title="How strongly this keyword fits your portfolio profile"
            >
              Suggested {Math.round(section.confidencePct)}%
            </Badge>
          )}
        </div>
        <span className="text-xs text-theme-text-secondary">
          Top {TOP_OPPORTUNITIES_PER_KEYWORD} for this keyword
        </span>
      </div>

      {sectionPending ? (
        <KeywordSectionSkeleton />
      ) : section.error ? (
        <p className="text-sm text-red-600 dark:text-red-400 py-4">
          Could not load opportunities for this keyword. Try refreshing the weekly review.
        </p>
      ) : (
        <>
          {(section.summaryLoading && !section.summary) || section.summary ? (
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
              <div className="flex items-center gap-1.5 mb-1">
                <Sparkles className="h-3.5 w-3.5 text-blue-600 dark:text-blue-300" aria-hidden="true" />
                <span className="text-xs font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">
                  Trend this week
                </span>
              </div>
              {section.summaryLoading && !section.summary ? (
                <p className="text-sm text-theme-text-secondary">Summarizing…</p>
              ) : (
                <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">{section.summary}</p>
              )}
            </div>
          ) : null}

          {section.opportunities.length === 0 ? (
            <p className="text-sm text-theme-text-secondary py-4">
              No matching opportunities this week for &ldquo;{section.keyword}&rdquo;.
            </p>
          ) : (
            <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
              {section.opportunities.map((opportunity) => {
                const govtOpp = mapSearchEntityToGovtOpportunity({
                  id: opportunity.id,
                  entity_type: 'govt_opportunity',
                  data: opportunity.data,
                  created_at: opportunity.created_at,
                  updated_at: opportunity.updated_at,
                });

                return (
                  <OpportunityDashboardCard
                    key={opportunity.id}
                    opportunity={govtOpp}
                    matchScore={getPersistedMatchScore(opportunity)}
                    onOpenDetails={onViewDetails}
                    onUpdateTracking={onUpdateTracking}
                    onGenerateProposal={onGenerateProposal}
                  />
                );
              })}
            </div>
          )}
        </>
      )}
    </section>
  );
}

export function GlobalOpportunitiesTab() {
  const { projectId } = useWorkspace();
  const { user, username } = useAuthContext();
  const queryClient = useQueryClient();
  const currentUserName = user?.full_name || username || user?.email || 'Unknown';
  const userId = user?.id || user?.email || 'anonymous';

  const trackedKeywordsState = useWeeklyReviewTrackedKeywords(projectId, userId);

  const {
    sections,
    highlights,
    pageSummary,
    isLoading,
    isFetching,
    opportunitiesReady,
    keywordsError,
    refetch,
    weekLabel,
    keywords,
    searchPoolEntities,
    selectedGeographicZone,
  } = useWeeklyGlobalOpportunities(projectId, {
    trackedKeywords: trackedKeywordsState.trackedKeywords,
  });

  const stalenessResync = useWeeklyReviewStalenessResync({
    projectId,
    entities: searchPoolEntities,
    enabled: opportunitiesReady && searchPoolEntities.length > 0,
  });

  const [selectedOpportunity, setSelectedOpportunity] = useState<GovtOpportunityEntity | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const totalOpportunities = useMemo(
    () =>
      opportunitiesReady
        ? sections.reduce((sum, section) => sum + section.opportunities.length, 0)
        : null,
    [sections, opportunitiesReady]
  );

  const updateTrackingMutation = useMutation({
    mutationFn: async ({
      entityId,
      trackingStatus,
    }: {
      entityId: string;
      trackingStatus: string;
    }) => {
      const api = getAPIClient();
      const statusValue = trackingStatus === '' ? null : trackingStatus;
      return api.put(`/entities/${entityId}`, {
        data: {
          tracking_status: statusValue,
          assigned_by: statusValue ? currentUserName : null,
        },
      });
    },
    onMutate: async ({ entityId, trackingStatus }) => {
      await queryClient.cancelQueries({ queryKey: ['weekly-global-opportunities'] });
      const statusValue = trackingStatus === '' ? null : trackingStatus;
      const assignedBy = statusValue ? currentUserName : null;
      queryClient.setQueriesData(
        { queryKey: ['weekly-global-opportunities'] },
        (old: unknown) =>
          patchWeeklyOpportunityTrackingData(
            old as { id?: unknown; data?: Record<string, unknown> | null }[] | undefined,
            entityId,
            { tracking_status: statusValue, assigned_by: assignedBy }
          )
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['weekly-global-opportunities'] });
      void queryClient.invalidateQueries({ queryKey: ['weekly-tracking-owners', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['govt-opportunities-all', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['govt-opportunities-page', projectId] });
    },
  });

  const handleViewDetails = useCallback((opportunity: GovtOpportunityEntity | OpportunityEntity) => {
    setSelectedOpportunity({
      id: opportunity.id,
      entity_type: 'govt_opportunity',
      data: opportunity.data,
      created_at: opportunity.created_at,
      updated_at: opportunity.updated_at,
    } as GovtOpportunityEntity);
    setIsModalOpen(true);
  }, []);

  const handleUpdateTracking = useCallback(
    (entityId: string, trackingStatus: string) => {
      updateTrackingMutation.mutate({ entityId, trackingStatus });
    },
    [updateTrackingMutation]
  );

  const handleGenerateProposal = useCallback((opportunity: GovtOpportunityEntity) => {
    const url = new URL(window.location.href);
    url.searchParams.set('tab', 'opportunities');
    url.searchParams.set('oppId', opportunity.id);
    window.history.pushState({}, '', url.toString());
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, []);

  const openOpportunitiesTab = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('tab', 'opportunities');
    window.history.pushState({}, '', url.toString());
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const openJobsPage = () => {
    window.location.assign('/jobs');
  };

  if (!projectId) {
    return (
      <EmptyState
        title="Project required"
        description="Select a workspace project to load your weekly keyword review."
      />
    );
  }

  const showInitialLoading = isLoading && sections.length === 0;
  const digestLoading = keywords.length > 0 && sections.every((section) => section.isLoading);

  return (
    <div className="weekly-review-tab space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-6 w-6 text-blue-600 dark:text-blue-400" aria-hidden="true" />
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
              Recommended Tenders
            </h2>
          </div>
          <p className="text-base text-slate-600 dark:text-slate-300 max-w-3xl">
            Top {TOP_OPPORTUNITIES_PER_KEYWORD} open tenders per tracked keyword, closing within
            the next {WEEKLY_REVIEW_DEADLINE_HORIZON_DAYS} days. Match % is SPARC portfolio match,
            the same score as Opportunities.
          </p>
          <div className="flex flex-wrap items-center gap-2 text-xs text-theme-text-secondary">
            <span>Week of {weekLabel}</span>
            <span aria-hidden>·</span>
            <span>{trackedKeywordsState.trackedKeywordCount} tracked keywords</span>
            <span aria-hidden>·</span>
            <span>{trackedKeywordsState.catalogCount} in catalog</span>
            <span aria-hidden>·</span>
            {totalOpportunities == null ? (
              <span className="inline-flex items-center gap-1">
                <LoadingSpinner />
                Loading opportunities…
              </span>
            ) : (
              <span>{totalOpportunities} opportunities surfaced</span>
            )}
            <span aria-hidden>·</span>
            <span>Region: {getGeographicZoneDisplayLabel(selectedGeographicZone)}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} aria-hidden="true" />
            Refresh Review
          </Button>
          <Button type="button" variant="primary" size="sm" onClick={openOpportunitiesTab}>
            <ExternalLink className="h-4 w-4 mr-2" aria-hidden="true" />
            Browse all opportunities
          </Button>
        </div>
      </div>

      {stalenessResync.bannerMessage ? (
        <p
          className="text-xs text-theme-text-secondary flex flex-wrap items-center gap-x-2 gap-y-1"
          role="status"
        >
          <span>{stalenessResync.bannerMessage}</span>
          <button
            type="button"
            onClick={openJobsPage}
            className="underline underline-offset-2 hover:text-gray-900 dark:hover:text-gray-100"
          >
            View jobs
          </button>
        </p>
      ) : null}

      {keywordsError ? (
        <p className="text-sm text-red-600 dark:text-red-400">
          Suggested keywords could not be loaded. Open Suggested Keywords on the Opportunities tab
          or refresh this review.
        </p>
      ) : showInitialLoading ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <LoadingSpinner />
          <p className="text-sm text-theme-text-secondary">
            Ranking top opportunities for your suggested keywords…
          </p>
        </div>
      ) : keywords.length === 0 ? (
        <EmptyState
          icon={<Sparkles className="h-10 w-10 text-gray-400" />}
          title="No suggested keywords yet"
          description="Generate suggested keywords from your project profile on the Opportunities tab, then return here for your weekly review."
        />
      ) : (
        <div className="space-y-8">
          {digestLoading ? (
            <div
              className="rounded-xl border border-slate-200 bg-white shadow-sm p-6 motion-safe:animate-pulse dark:border-slate-700 dark:bg-slate-900"
              aria-hidden="true"
            >
              <div className="h-4 w-40 rounded bg-slate-100 dark:bg-slate-800 mb-2" />
              <div className="h-3 w-full rounded bg-slate-100 dark:bg-slate-800" />
            </div>
          ) : pageSummary ? (
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-6 dark:border-slate-700 dark:bg-slate-900">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="h-4 w-4 text-blue-600 dark:text-blue-300" aria-hidden="true" />
                <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  Weekly outlook
                </h3>
              </div>
              <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">{pageSummary}</p>
            </div>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-3">
            <DigestStrip
              title="Best across keywords"
              icon={<Star className="h-4 w-4 text-amber-500" aria-hidden="true" />}
              items={highlights.topPicks}
              isLoading={digestLoading}
              emptyLabel="No cross-keyword picks in this week's shortlist."
              onViewDetails={handleViewDetails}
            />
            <DigestStrip
              title="New this week"
              icon={<Flame className="h-4 w-4 text-orange-500" aria-hidden="true" />}
              items={highlights.newThisWeek}
              isLoading={digestLoading}
              emptyLabel="No newly published opportunities in this week’s shortlist."
              onViewDetails={handleViewDetails}
            />
            <DigestStrip
              title="Expiring soon"
              icon={<Clock3 className="h-4 w-4 text-red-500" aria-hidden="true" />}
              items={highlights.expiringSoon}
              isLoading={digestLoading}
              emptyLabel={`Nothing closing in the next ${WEEKLY_REVIEW_EXPIRING_DAYS} days in this shortlist.`}
              onViewDetails={handleViewDetails}
            />
          </div>

          {sections.map((section) => (
            <KeywordSection
              key={section.keyword}
              section={section}
              onViewDetails={handleViewDetails}
              onUpdateTracking={handleUpdateTracking}
              onGenerateProposal={handleGenerateProposal}
            />
          ))}

          <div className="space-y-3 pt-2 border-t border-theme-border/60">
            <p className="text-[11px] uppercase tracking-wide text-theme-text-secondary">
              Review settings
            </p>
            <WeeklyReviewTrackedKeywordsPanel
              catalog={trackedKeywordsState.catalog}
              filteredCatalog={trackedKeywordsState.filteredCatalog}
              draftSelectionSet={trackedKeywordsState.draftSelectionSet}
              draftSelectedCount={trackedKeywordsState.draftSelectedCount}
              catalogCount={trackedKeywordsState.catalogCount}
              trackedKeywordCount={trackedKeywordsState.trackedKeywordCount}
              filterText={trackedKeywordsState.filterText}
              customInput={trackedKeywordsState.customInput}
              isDirty={trackedKeywordsState.isDirty}
              isLoadingCatalog={trackedKeywordsState.isLoadingCatalog}
              catalogError={trackedKeywordsState.catalogError}
              onFilterTextChange={trackedKeywordsState.setFilterText}
              onCustomInputChange={trackedKeywordsState.setCustomInput}
              onToggleKeyword={trackedKeywordsState.toggleKeyword}
              onSelectAll={trackedKeywordsState.selectAll}
              onClearAll={trackedKeywordsState.clearAll}
              onAddCustomKeyword={trackedKeywordsState.addCustomKeyword}
              onSave={trackedKeywordsState.save}
              onResetDraft={trackedKeywordsState.resetDraft}
            />
            <RecentlyExpiredStrip
              items={highlights.recentlyExpiredMissed}
              isLoading={digestLoading}
              onViewDetails={handleViewDetails}
            />
          </div>
        </div>
      )}

      <OpportunityDetailModal
        opportunity={selectedOpportunity}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedOpportunity(null);
        }}
      />
    </div>
  );
}

